import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { List } from "react-window";
import { GitCommit } from "lucide-react";
import { parseDiffLines } from "./DiffDisplay";

// Virtualized row component for diff lines (reused from DiffDisplay)
const DiffRow = ({ index, style, lines, getChangeIcon, getChangeColor }) => {
    const line = lines[index];

    if (line.type === "hunk-header") {
        return (
            <div
                style={{ ...style, padding: "4px 12px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#f0f4f8" }}
            >
                <div className="text-xs font-mono text-gray-500">{line.content}</div>
            </div>
        );
    }

    return (
        <div
            style={{
                ...style,
                padding: "8px 12px",
                borderBottom: "1px solid #f3f4f6",
                backgroundColor: getChangeColor(line.type).includes("green")
                    ? "#f0fdf4"
                    : getChangeColor(line.type).includes("red")
                    ? "#fef2f2"
                    : "#f9fafb",
            }}
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className="flex-shrink-0 w-8 text-center text-xs text-gray-500">
                    {line.lineNumber}
                </div>
                <div className="flex-shrink-0 w-6 text-center">
                    {getChangeIcon(line.type)}
                </div>
                <div className="flex-1 min-w-0">
                    <div
                        className={`text-xs font-mono whitespace-pre overflow-x-auto ${
                            line.type === "addition"
                                ? "text-green-600"
                                : line.type === "deletion"
                                ? "text-red-600"
                                : "text-gray-600"
                        }`}
                        style={{
                            lineHeight: "1.4",
                            maxWidth: "100%",
                        }}
                    >
                        {line.content || " "}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DiffModal = ({ isOpen, onClose, selectedDiff }) => {
    if (!selectedDiff) return null;

    const diffLines = parseDiffLines(selectedDiff);

    const getChangeIcon = (type) => {
        switch (type) {
            case "addition":
                return <span className="text-green-600 font-bold">+</span>;
            case "deletion":
                return <span className="text-red-600 font-bold">-</span>;
            case "context":
                return <span className="text-gray-400 font-bold">·</span>;
            default:
                return null;
        }
    };

    const getChangeColor = (type) => {
        switch (type) {
            case "addition":
                return "bg-green-50 border-green-200";
            case "deletion":
                return "bg-red-50 border-red-200";
            case "context":
                return "bg-gray-50 border-gray-200";
            default:
                return "bg-gray-50 border-gray-200";
        }
    };

    const additionsCount = diffLines.filter(
        (line) => line.type === "addition"
    ).length;
    const deletionsCount = diffLines.filter(
        (line) => line.type === "deletion"
    ).length;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0">
                <DialogHeader className="px-6 py-4 border-b">
                    <DialogTitle className="flex items-center gap-2">
                        <GitCommit className="h-5 w-5 text-gray-600" />
                        <span>Code Changes</span>
                        {additionsCount > 0 && (
                            <Badge
                                variant="outline"
                                className="text-xs text-green-600 border-green-300 hidden md:inline-block"
                            >
                                +{additionsCount}
                            </Badge>
                        )}
                        {deletionsCount > 0 && (
                            <Badge
                                variant="outline"
                                className="text-xs text-red-600 border-red-300 hidden md:inline-block"
                            >
                                -{deletionsCount}
                            </Badge>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-hidden">
                    {diffLines.length > 0 ? (
                        <div className="h-full bg-white">
                            <List
                                height={window.innerHeight - 120}
                                rowCount={diffLines.length}
                                rowHeight={32}
                                rowComponent={DiffRow}
                                rowProps={{
                                    lines: diffLines,
                                    getChangeIcon,
                                    getChangeColor,
                                }}
                                className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                            />
                        </div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DiffModal;
